import { api } from "jiffy-api";
import { getUploadTimeout } from "common/upload-constants";
import { downscaleClaimImage } from "common/claim-image-downscale";
import { notifyError } from "common/utils/app-monitoring/notify";

import "./claim-image-uploader.scss";

const $getUploadButton = () => document.querySelector(".js-claim-images-button");
const $getUploaderInfoContainer = () => document.querySelector(".js-claim-uploader-info");
const $getUploadInfoLabel = () => document.querySelector(".js-claim-upload-info-label");
const $getImageFilesSelector = () => document.querySelector(".js-claim-image-files");
const $getImageIdsField = () => document.querySelector(".js-claim-image-ids");
const $getDiscardButton = () => document.querySelector(".js-claim-discard-button");

// PO-1366: keep keys aligned with `record_rejection`'s reason symbol in
// app/controllers/spree/requests/claims/requests_controller.rb.
// Mirror in frontend/components/supplier_admin/made/create-claim/image-upload-handler.js.
const REJECTION_MESSAGES = {
  unsupported_format: "unsupported file format",
  too_large: "file is too large (50 MB max) — upload one photo at a time or reduce its resolution",
  mime_mismatch: "file content does not match its declared format",
  live_photo_video:
    "this looks like an iPhone Live Photo or video. Open the photo, tap Share → " +
    '"Duplicate as Still Photo" (or turn Live off), then upload the still image',
  sanitize_failed: "SVG could not be sanitized for safe display",
  upload_failed: "upload failed — please try again"
};

const formatRejection = ({ filename, reason }) => {
  const label = REJECTION_MESSAGES[reason] || reason || "rejected";
  return `${filename}: ${label}`;
};

const claimUploadTimeout = files =>
  getUploadTimeout(Array.from(files).reduce((sum, f) => sum + (f.size || 0), 0));

// MN-8909: gated by the claim_client_image_downscale flag, rendered onto the
// file input. Off => files upload raw exactly as before.
const isDownscaleEnabled = () => $getImageFilesSelector().dataset.clientDownscale === "true";

// MN-10448: the claim upload path is the only image-upload flow that never
// reported failures to Honeybadger — the catch block logged to console only,
// and a 200 with zero usable image_ids surfaced only as a label. That left a
// customer-blocking, mobile-skewed upload failure invisible to observability.
// This bounds a diagnostic context (no PII: filenames excluded, only shapes)
// so the real failure — client-side reject, server rejection reason, HTTP
// status, or timeout — is queryable. Fixed fingerprints keep HB cardinality
// bounded despite per-notice UA/type variation.
const uploadDiagnosticContext = files => {
  const list = Array.from(files || []);
  return {
    file_count: list.length,
    file_types: list.map(file => file.type || "(empty)"),
    file_sizes: list.map(file => file.size),
    total_bytes: list.reduce((sum, file) => sum + (file.size || 0), 0),
    downscale_enabled: isDownscaleEnabled(),
    user_agent: navigator.userAgent
  };
};

// Best-effort: downscaleClaimImage never throws and returns the original file
// on any failure, so a failed downscale still uploads the untouched file.
const prepareFile = async file => {
  const { file: prepared, status } = await downscaleClaimImage(file);
  if (status === "failed") {
    notifyError("Claim image client-side downscale failed", {
      // Bound the user-controlled filename before it reaches monitoring.
      params: { filename: file.name.slice(0, 256), type: file.type, size: file.size }
    });
  }
  return prepared;
};

// Expected client-side validation error (not a genuine upload failure). Kept as
// a shared sentinel so the catch block can distinguish it from real failures and
// skip Honeybadger reporting — otherwise this normal user error would muddy the
// claim-image-upload-failed fingerprint (review: jimryanzulueta on PR #60856).
const UNSUPPORTED_FILE_TYPE_MESSAGE = "You can't upload files of this type.";

// MN-5719: forward order ownership proof so the controller can authorize the
// upload. Hidden fields live on the parent claim form.
const buildFormData = async files => {
  const formData = new FormData();
  const orderNumberField = document.querySelector("input[name='order_number']");
  const lastNameField = document.querySelector("input[name='last_name']");
  if (orderNumberField) formData.append("order_number", orderNumberField.value);
  if (lastNameField) formData.append("last_name", lastNameField.value);

  const fileList = Array.from(files);
  fileList.forEach(file => {
    if (!file.type.startsWith("image/")) {
      throw new Error(UNSUPPORTED_FILE_TYPE_MESSAGE);
    }
  });

  // Sequential by design: downscaling rasterizes onto a canvas, so processing
  // several large images concurrently can spike memory and OOM mobile browsers.
  const downscale = isDownscaleEnabled();
  for (const file of fileList) {
    // eslint-disable-next-line no-await-in-loop
    formData.append("images[]", downscale ? await prepareFile(file) : file);
  }
  return formData;
};

const applyUploadResponse = (response, uploadInfoLabel, files) => {
  const imageIds = Array.isArray(response.image_ids) ? response.image_ids : [];
  const rejected = Array.isArray(response.rejected) ? response.rejected : [];

  if (rejected.length > 0) {
    // eslint-disable-next-line no-alert
    alert(`Some files were rejected:\n\n${rejected.map(formatRejection).join("\n")}`);
  }

  if (imageIds.length === 0) {
    // Single-batch UI: the upload button hides on first attempt (success or
    // failure) and discard clears the field before any retry, so no prior IDs
    // can be in the field here. Skip the explicit clear as defensive behavior.
    uploadInfoLabel.innerText =
      rejected.length > 0
        ? "No files were accepted. Please try a different file."
        : "Upload failed — please try again.";

    // MN-10448: a 200 with zero usable image_ids is a customer-blocking outcome
    // (every file rejected server-side, or an empty response). Report it with
    // the server's rejection reasons so we can see WHY iOS uploads yield nothing.
    notifyError("Claim image upload returned no usable images", {
      params: {
        rejected_reasons: rejected.map(entry => entry.reason),
        rejected_count: rejected.length,
        ...uploadDiagnosticContext(files)
      },
      fingerprint: "claim-image-upload-no-usable-images"
    });
  } else {
    uploadInfoLabel.innerText = `${imageIds.length} images uploaded`;
    $getImageIdsField().value = imageIds.join(",");
  }
};

const uploadFiles = async (files, uploadInfoLabel) =>
  api.ordersClaims.uploadImages(await buildFormData(files), {
    raw: true,
    useAxiosForRequest: true,
    // MN-10448: axiosRequest already calls reportRequestError on status-bearing
    // HTTP errors (request.js:222). Silence that generic notice so the single
    // report for a claim upload failure is the claim-specific one below (with
    // rejection-reason/file context), not two HB faults (review: codex/fauzi-ar).
    silent: true,
    headers: { "Content-Type": undefined },
    timeout: claimUploadTimeout(files),
    onUploadProgress: ({ progress }) => {
      const currProgress = (progress * 100).toFixed();
      uploadInfoLabel.innerText = `Uploading ${currProgress}%`;
    }
  });

const handleImagesSelected = async event => {
  event.preventDefault();

  const { files } = event.target;

  if (!files.length) return;

  const uploadInfoLabel = $getUploadInfoLabel();

  $getUploadButton().classList.add("hidden");
  $getUploaderInfoContainer().classList.remove("hidden");
  uploadInfoLabel.innerText = "Uploading...";

  try {
    const response = await uploadFiles(files, uploadInfoLabel);
    applyUploadResponse(response, uploadInfoLabel, files);
  } catch (error) {
    uploadInfoLabel.innerText = error.message;

    // MN-10448: previously console-only, so transport failures, HTTP errors
    // (e.g. 401 order-verification), and timeouts on the claim path never
    // reached Honeybadger. Capture the error shape + HTTP status/body so the
    // blocking, iOS-skewed failure is diagnosable. Skip the expected client-side
    // type-validation reject — it is normal user error, not an upload failure,
    // and would muddy this fingerprint (review: jimryanzulueta).
    if (error.message !== UNSUPPORTED_FILE_TYPE_MESSAGE) {
      notifyError("Claim image upload failed", {
        params: {
          error_message: error.message,
          error_name: error.name,
          http_status: error.response?.status ?? null,
          response_body: error.response?.data ?? null,
          ...uploadDiagnosticContext(files)
        },
        fingerprint: "claim-image-upload-failed"
      });
    }
    console.error("Claim image upload: ", error);
  } finally {
    $getDiscardButton().classList.remove("hidden");
  }
};

const handleUploadButtonClick = event => {
  event.preventDefault();

  $getImageFilesSelector().click();
};

const handleDiscardButtonClick = event => {
  event.preventDefault();

  $getUploadButton().classList.remove("hidden");
  $getUploaderInfoContainer().classList.add("hidden");
  $getDiscardButton().classList.add("hidden");

  $getImageIdsField().value = "";
  $getImageFilesSelector().value = "";
};

const initClaimImageUploader = () => {
  $getImageFilesSelector().addEventListener("change", handleImagesSelected);
  $getUploadButton().addEventListener("click", handleUploadButtonClick);
  $getDiscardButton().addEventListener("click", handleDiscardButtonClick);
};

export default initClaimImageUploader;
